import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, updateDoc, doc, deleteDoc, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import PayrollProfileModal from "./PayrollProfile";
import { QRCodeSVG } from "qrcode.react";

// 新しい社員に配る登録用URL。会社コードが入った状態で登録画面が開く。
// 登録した人は「承認待ち」になるので、URLが広まっても勝手には入れない。
function InviteCard({ companyId }) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const url = `https://miyabicompany-manager.web.app/?signup=1&type=employee&code=${encodeURIComponent(companyId)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // クリップボードが使えない環境では、手で選択してもらう
      alert("コピーできませんでした。URLを選択してコピーしてください。");
    }
  };

  return (
    <div style={st.invite}>
      <div style={st.inviteTitle}>🔗 新しい社員を招待する</div>
      <p style={st.inviteDesc}>
        このURLをメールやメッセージで送ると、会社コードが入った状態で登録画面が開きます。
        送られた方は氏名や連絡先を入力するだけで登録できます。
      </p>
      <input style={st.inviteUrl} value={url} readOnly onFocus={e => e.target.select()} />
      <div style={st.inviteBtns}>
        <button style={st.copyBtn} onClick={copy}>{copied ? "✅ コピーしました" : "URLをコピー"}</button>
        <button style={st.qrBtn} onClick={() => setShowQr(v => !v)}>
          {showQr ? "QRコードを隠す" : "QRコードを表示"}
        </button>
      </div>
      {showQr && (
        <div style={st.qrWrap}>
          <QRCodeSVG value={url} size={168} />
          <p style={st.inviteDesc}>スマホのカメラで読み取ってもらえます。</p>
        </div>
      )}
      <p style={st.inviteNote}>
        登録された方は「承認待ち」になります。下の一覧で承認するまで、社内の情報は見られません。
        そのため、このURLが他の人に伝わっても問題ありません。
      </p>
    </div>
  );
}


const PERMISSION_LABELS = {
  postNotice: "お知らせ投稿・削除",
  approveDocument: "書類申請の承認・却下",
  distributePayslip: "給与明細の配布",
  manageCompanyDocs: "ライブラリーの掲載・削除",
  manageUsers: "社員管理（承認・権限設定）",
  manageDevices: "打刻端末の承認・管理",
  viewAllAttendance: "全社員の勤怠閲覧",
  companyAdmin: "会社管理者（全権限）",
};

function UserManagement({ companyId, isAdmin, canPayroll = false, user }) {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [kioskPending, setKioskPending] = useState([]);
  const [kioskApproved, setKioskApproved] = useState([]);
  const [tab, setTab] = useState("pending");
  const [payrollMap, setPayrollMap] = useState({});
  const [assigning, setAssigning] = useState(false);
  const [editingName, setEditingName] = useState(null);   // 氏名を編集中のユーザー
  const [nameForm, setNameForm] = useState({ lastName: "", firstName: "", lastNameKana: "", firstNameKana: "", phone: "" });
  const [editingPayroll, setEditingPayroll] = useState(null);

  // 給与情報を見られるのは会社管理者と「給与明細の配布」権限を持つ人だけ
  const canEditPayroll = isAdmin || canPayroll;
  const [editingUser, setEditingUser] = useState(null);
  const [editPerms, setEditPerms] = useState({});

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    let q;
    if (companyId) {
      q = query(collection(db, "users"), where("companyId", "==", companyId));
    } else {
      q = collection(db, "users");
    }
    const snap = await getDocs(q);
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setPendingUsers(all.filter(u => u.status === "pending").sort((a, b) => b.createdAt - a.createdAt));
    // フリガナがあれば五十音順、無ければ氏名で並べる
    const sortKey = (u) => u.nameKana || u.name || "";
    setApprovedUsers(all.filter(u => u.status === "approved").sort((a, b) => sortKey(a).localeCompare(sortKey(b), "ja")));

    let kq;
    if (companyId) {
      kq = query(collection(db, "kioskUsers"), where("companyId", "==", companyId));
    } else {
      kq = collection(db, "kioskUsers");
    }
    const kSnap = await getDocs(kq);
    const kAll = kSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setKioskPending(kAll.filter(u => u.status === "pending"));
    setKioskApproved(kAll.filter(u => u.status === "approved"));

    if (canEditPayroll) {
      try {
        const pq = companyId
          ? query(collection(db, "payrollProfiles"), where("companyId", "==", companyId))
          : collection(db, "payrollProfiles");
        const pSnap = await getDocs(pq);
        const map = {};
        pSnap.docs.forEach(d => { map[d.id] = d.data(); });
        setPayrollMap(map);
      } catch (e) {
        console.error("給与情報の取得に失敗しました:", e);
      }
    }
  };

  const handleApprove = async (u) => {
    await updateDoc(doc(db, "users", u.id), {
      status: "approved",
      permissions: u.permissions || { postNotice: false, approveDocument: false, distributePayslip: false, manageCompanyDocs: false, manageUsers: false, manageDevices: false, viewAllAttendance: false, companyAdmin: false },
    });
    fetchUsers();
  };

  const handleReject = async (id) => {
    if (!window.confirm("却下（削除）しますか？")) return;
    try {
      await deleteDoc(doc(db, "users", id));
    } catch (e) {
      console.error("削除に失敗しました:", e);
      alert("削除できませんでした。会社管理者の削除は、会社管理者だけが行えます。");
      return;
    }
    fetchUsers();
  };

  const handleKioskApprove = async (id) => {
    await updateDoc(doc(db, "kioskUsers", id), { status: "approved" });
    fetchUsers();
  };

  const handleKioskDelete = async (id) => {
    if (!window.confirm("削除しますか？")) return;
    await deleteDoc(doc(db, "kioskUsers", id));
    fetchUsers();
  };

  // 社員IDが無い既存社員へまとめて採番する
  const handleAssignMemberNos = async () => {
    const targets = [...pendingUsers, ...approvedUsers].filter(u => !u.memberNo);
    if (targets.length === 0) return;
    if (!window.confirm(`社員IDが未設定の${targets.length}名に採番します。よろしいですか？`)) return;
    setAssigning(true);
    try {
      const call = httpsCallable(getFunctions(undefined, "us-central1"), "allocateMemberNo");
      for (const u of targets) {
        try {
          const res = await call({ companyId: u.companyId || companyId });
          await updateDoc(doc(db, "users", u.id), { memberNo: res.data.memberNo });
        } catch (e) {
          console.error("採番に失敗しました:", u.email, e);
        }
      }
      await fetchUsers();
    } finally {
      setAssigning(false);
    }
  };

  const openNameEdit = (u) => {
    setNameForm({
      lastName:      u.lastName || "",
      firstName:     u.firstName || "",
      lastNameKana:  u.lastNameKana || "",
      firstNameKana: u.firstNameKana || "",
      phone:         u.phone || "",
    });
    setEditingName(u);
  };

  const handleSaveName = async () => {
    const f = nameForm;
    const name     = [f.lastName.trim(), f.firstName.trim()].filter(Boolean).join(" ");
    const nameKana = [f.lastNameKana.trim(), f.firstNameKana.trim()].filter(Boolean).join(" ");
    if (!name) { alert("姓または名を入力してください"); return; }
    await updateDoc(doc(db, "users", editingName.id), {
      name, nameKana,
      lastName: f.lastName.trim(),
      firstName: f.firstName.trim(),
      lastNameKana: f.lastNameKana.trim(),
      firstNameKana: f.firstNameKana.trim(),
      phone: f.phone.trim(),
    });
    setEditingName(null);
    fetchUsers();
  };

  const openPermEdit = (u) => {
    setEditingUser(u);
    setEditPerms(u.permissions || {});
  };

  const handleSavePerms = async () => {
    try {
      await updateDoc(doc(db, "users", editingUser.id), { permissions: editPerms });
    } catch (e) {
      // ルールで断られたとき（会社管理者の印の付け外しは、会社管理者だけができる）
      console.error("権限の保存に失敗しました:", e);
      alert(e?.code === "permission-denied"
        ? "保存できませんでした。「会社管理者（全権限）」の付け外しと、会社管理者の権限の変更は、会社管理者だけが行えます。"
        : "保存できませんでした。もう一度お試しください。");
      return;
    }
    setEditingUser(null);
    fetchUsers();
  };

  const fmt = (ts) => ts?.toDate?.().toLocaleDateString("ja-JP") || "";
  const totalPending = pendingUsers.length + kioskPending.length;

  return (
    <div style={st.container}>
      <h2 style={st.title}>👥 社員管理</h2>
      {isAdmin && companyId && <InviteCard companyId={companyId} />}
      <div style={st.tabs}>
        <button style={{ ...st.tab, ...(tab === "pending" ? st.active : {}) }} onClick={() => setTab("pending")}>
          承認待ち {totalPending > 0 && <span style={st.badge}>{totalPending}</span>}
        </button>
        <button style={{ ...st.tab, ...(tab === "approved" ? st.active : {}) }} onClick={() => setTab("approved")}>承認済み一覧</button>
      </div>

      {tab === "pending" && (
        <>
          {totalPending === 0 && <p style={st.empty}>承認待ちのユーザーはいません</p>}
          {kioskPending.length > 0 && (
            <>
              <h3 style={st.section}>📱 QR打刻ユーザー</h3>
              {kioskPending.map(u => (
                <div key={u.id} style={st.card}>
                  <div><div style={st.name}>{u.name}</div><div style={st.sub}>📞 {u.phone}</div><div style={st.sub}>{fmt(u.createdAt)}</div></div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button style={st.approveBtn} onClick={() => handleKioskApprove(u.id)}>承認</button>
                    <button style={st.rejectBtn} onClick={() => handleKioskDelete(u.id)}>却下</button>
                  </div>
                </div>
              ))}
            </>
          )}
          {pendingUsers.length > 0 && (
            <>
              <h3 style={st.section}>💻 アプリログインユーザー</h3>
              {pendingUsers.map(u => (
                <div key={u.id} style={st.card}>
                  <div><div style={st.name}>{u.name || "（氏名未設定）"}</div><div style={st.sub}>{u.email}</div>{u.phone && <div style={st.sub}>📞 {u.phone}</div>}</div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button style={st.approveBtn} onClick={() => handleApprove(u)}>承認</button>
                    <button style={st.rejectBtn} onClick={() => handleReject(u.id)}>却下</button>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {tab === "approved" && (
        <>
          {kioskApproved.length === 0 && approvedUsers.length === 0 && <p style={st.empty}>承認済みのユーザーはいません</p>}
          {kioskApproved.length > 0 && (
            <>
              <h3 style={st.section}>📱 QR打刻ユーザー</h3>
              <table style={st.table}>
                <thead><tr><th style={st.th}>氏名</th><th style={st.th}>電話番号</th><th style={st.th}>操作</th></tr></thead>
                <tbody>
                  {kioskApproved.map(u => (
                    <tr key={u.id}>
                      <td style={st.td}>{u.name}</td>
                      <td style={st.td}>{u.phone}</td>
                      <td style={st.td}><button style={st.deleteBtn} onClick={() => handleKioskDelete(u.id)}>削除</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {[...pendingUsers, ...approvedUsers].some(u => !u.memberNo) && (
            <div style={st.assignBar}>
              <span>社員IDが未採番の方がいます。取り違え防止のため付与しておくことをおすすめします。</span>
              <button style={st.assignBtn} onClick={handleAssignMemberNos} disabled={assigning}>
                {assigning ? "採番中..." : "まとめて採番する"}
              </button>
            </div>
          )}
          {approvedUsers.length > 0 && (
            <>
              <h3 style={st.section}>💻 アプリログインユーザー</h3>
              {approvedUsers.map(u => (
                <div key={u.id} style={st.userCard}>
                  <div style={st.userTop}>
                    <div>
                      <div style={st.name}>{u.name || "－"}</div>
                      <div style={u.nameKana ? st.kana : st.kanaNone}>
                        {u.nameKana || "フリガナ未登録"}
                      </div>
                      <div style={st.sub}>{u.email}</div>
                      <div style={u.memberNo ? st.memberNo : st.memberNoNone}>
                        {u.memberNo ? `社員ID：${u.memberNo}` : "社員ID：未採番"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                      <button style={st.payrollBtn} onClick={() => openNameEdit(u)}>氏名編集</button>
                      {canEditPayroll && <button style={st.payrollBtn} onClick={() => setEditingPayroll(u)}>給与情報</button>}
                      <button style={st.editBtn} onClick={() => openPermEdit(u)}>権限設定</button>
                    </div>
                  </div>
                  {canEditPayroll && (
                    <div style={payrollMap[u.id] ? st.payrollSet : st.payrollUnset}>
                      💴 {payrollMap[u.id]
                        ? `給与情報 登録済み${fmt(payrollMap[u.id].updatedAt) ? `（最終更新 ${fmt(payrollMap[u.id].updatedAt)}）` : ""}`
                        : "給与情報 未設定"}
                    </div>
                  )}
                  <div style={st.permTags}>
                    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                      <span key={key} style={{ ...st.permTag, backgroundColor: u.permissions?.[key] ? "#d5f5e3" : "#f0f0f0", color: u.permissions?.[key] ? "#27ae60" : "#aaa" }}>
                        {u.permissions?.[key] ? "✅" : "—"} {label}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {editingName && (
        <div style={st.overlay}>
          <div style={st.modal}>
            <h3 style={{ color: "#2c3e50", marginBottom: "4px" }}>氏名の編集</h3>
            <p style={{ color: "#7f8c8d", fontSize: "13px", marginTop: 0, marginBottom: "16px" }}>{editingName.email}</p>

            <label style={st.formLabel}>氏名</label>
            <div style={st.nameRow}>
              <input style={st.input} placeholder="姓（山田）" value={nameForm.lastName}
                onChange={e => setNameForm({ ...nameForm, lastName: e.target.value })} />
              <input style={st.input} placeholder="名（太郎）" value={nameForm.firstName}
                onChange={e => setNameForm({ ...nameForm, firstName: e.target.value })} />
            </div>

            <label style={st.formLabel}>フリガナ</label>
            <div style={st.nameRow}>
              <input style={st.input} placeholder="セイ（ヤマダ）" value={nameForm.lastNameKana}
                onChange={e => setNameForm({ ...nameForm, lastNameKana: e.target.value })} />
              <input style={st.input} placeholder="メイ（タロウ）" value={nameForm.firstNameKana}
                onChange={e => setNameForm({ ...nameForm, firstNameKana: e.target.value })} />
            </div>

            <label style={st.formLabel}>電話番号</label>
            <input style={st.input} placeholder="09012345678" value={nameForm.phone}
              onChange={e => setNameForm({ ...nameForm, phone: e.target.value })} />

            <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
              <button style={st.approveBtn} onClick={handleSaveName}>保存</button>
              <button style={st.cancelBtn} onClick={() => setEditingName(null)}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {editingPayroll && (
        <PayrollProfileModal
          targetUser={editingPayroll}
          companyId={companyId}
          editorEmail={user?.email}
          onClose={() => setEditingPayroll(null)}
          onSaved={(uid, data) => setPayrollMap(m => ({ ...m, [uid]: data }))}
        />
      )}

      {editingUser && (
        <div style={st.overlay}>
          <div style={st.modal}>
            <h3 style={{ color: "#2c3e50", marginBottom: "16px" }}>権限設定：{editingUser.name}</h3>
            {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
              <label key={key} style={st.checkLabel}>
                <input type="checkbox" checked={editPerms[key] || false} onChange={e => setEditPerms({ ...editPerms, [key]: e.target.checked })} style={{ marginRight: "10px", width: "18px", height: "18px" }} />
                {label}
              </label>
            ))}
            <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
              <button style={st.approveBtn} onClick={handleSavePerms}>保存</button>
              <button style={st.cancelBtn} onClick={() => setEditingUser(null)}>キャンセル</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "800px", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "24px" },
  invite:      { backgroundColor: "#fff", border: "1px solid #e6ebf0", borderRadius: "12px", padding: "20px 22px", marginBottom: "24px" },
  inviteTitle: { fontSize: "15px", fontWeight: "bold", color: "#2c3e50", marginBottom: "8px" },
  inviteDesc:  { fontSize: "13px", color: "#7f8c8d", lineHeight: "1.9", margin: "0 0 12px" },
  inviteUrl:   { width: "100%", padding: "10px 12px", border: "1px solid #dfe6ec", borderRadius: "8px", fontSize: "13px", color: "#2c3e50", backgroundColor: "#f7f9fb", boxSizing: "border-box" },
  inviteBtns:  { display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" },
  copyBtn:     { padding: "9px 18px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  qrBtn:       { padding: "9px 18px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #2c3e50", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  qrWrap:      { textAlign: "center", marginTop: "16px" },
  inviteNote:  { fontSize: "12px", color: "#7f8c8d", lineHeight: "1.8", backgroundColor: "#f7f9fb", borderRadius: "8px", padding: "10px 12px", marginTop: "12px", marginBottom: 0 },
  section: { color: "#2c3e50", marginBottom: "12px", marginTop: "8px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "24px" },
  tab: { padding: "10px 20px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d" },
  active: { border: "2px solid #2c3e50", color: "#2c3e50", fontWeight: "bold" },
  badge: { backgroundColor: "#e74c3c", color: "#fff", borderRadius: "10px", padding: "2px 7px", fontSize: "12px", marginLeft: "6px" },
  empty: { color: "#999", textAlign: "center", marginTop: "40px" },
  card: { backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  name: { fontSize: "16px", fontWeight: "bold", color: "#2c3e50", marginBottom: "4px" },
  sub: { fontSize: "13px", color: "#7f8c8d" },
  memberNo: { fontSize: "12px", color: "#2980b9", fontFamily: "monospace", marginTop: "4px" },
  kana: { fontSize: "12px", color: "#7f8c8d", marginBottom: "2px" },
  kanaNone: { fontSize: "12px", color: "#e67e22", marginBottom: "2px" },
  formLabel: { display: "block", marginBottom: "6px", color: "#2c3e50", fontSize: "13px", fontWeight: "bold" },
  input: { width: "100%", padding: "10px 12px", marginBottom: "14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  nameRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" },
  memberNoNone: { fontSize: "12px", color: "#e67e22", marginTop: "4px" },
  assignBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "10px", padding: "14px 18px", marginBottom: "16px", fontSize: "13px", color: "#8a5a00" },
  assignBtn: { padding: "8px 18px", backgroundColor: "#e67e22", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", flexShrink: 0 },
  approveBtn: { padding: "8px 20px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  rejectBtn: { padding: "8px 20px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  deleteBtn: { padding: "4px 10px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" },
  editBtn: { padding: "6px 16px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  payrollBtn: { padding: "6px 16px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #2c3e50", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  payrollSet: { fontSize: "13px", color: "#2c3e50", backgroundColor: "#f0f4f8", padding: "8px 12px", borderRadius: "8px", marginBottom: "12px" },
  payrollUnset: { fontSize: "13px", color: "#e67e22", backgroundColor: "#fff6e5", padding: "8px 12px", borderRadius: "8px", marginBottom: "12px" },
  cancelBtn: { padding: "8px 20px", backgroundColor: "#95a5a6", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  userCard: { backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  userTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" },
  permTags: { display: "flex", flexWrap: "wrap", gap: "8px" },
  permTag: { padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold" },
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", borderRadius: "12px", overflow: "hidden", marginBottom: "24px" },
  th: { backgroundColor: "#2c3e50", color: "#fff", padding: "12px", textAlign: "left", fontSize: "14px" },
  td: { padding: "10px 12px", borderBottom: "1px solid #eee", fontSize: "14px" },
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { backgroundColor: "#fff", padding: "32px", borderRadius: "16px", width: "400px", maxWidth: "90vw" },
  checkLabel: { display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #eee", fontSize: "15px", color: "#2c3e50", cursor: "pointer" },
};

export default UserManagement;
